import {Matter, MatterAttractors, gsap, ScrollTrigger} from "../LibrariesImport";

Matter.Common.logLevel = 0;
import {GlobalVariables} from "../GlobalVariables";

export default class AnimationFooter {
	static init() {
		if (ScrollTrigger.isTouch === 1) {
			return;
		}

		this.footerMain = document.querySelector(".footer-main");
		if (!this.footerMain) {
			return;
		}
		this.keywordsContainer = this.footerMain.querySelector('.footer-keywords');
		if (this.keywordsContainer) {
			this.initOnFooterEnter();
			GlobalVariables.resizeCallbacks.push(this);
		}
	}

	static initOnFooterEnter() {
		ScrollTrigger.create({
			trigger: '.footer-main',
			start: 'top 90%', // Lorsque le haut de '.footer-main' atteint 90% bas du viewport
			onEnter: () => {
				// Lancer le rendu lorsque .footer-main entre dans la vue pour la première fois
				this.renderCanvas();
			},
			once: true // Assurer que le déclenchement n'a lieu qu'une seule fois
		});


		let mousewheelTimeout;
		this.footerMain.addEventListener('wheel', (event) => {
			this.keywordsContainer.classList.add('inactive');

			// Clear the existing timeout if the 'wheel' event is still being fired
			clearTimeout(mousewheelTimeout);

			// Set a timeout to trigger the "stop" logic after 200ms of inactivity
			mousewheelTimeout = setTimeout(() => {
				this.keywordsContainer.classList.remove('inactive');
			}, 200);
		}, {passive: true});
	}

	static canvasSize() {
		this.canvasSizes = {
			width: this.keywordsContainer.offsetWidth,
			height: this.keywordsContainer.offsetHeight
		};
	}

	static renderCanvas() {
		this.canvasSize();

		this.Engine = Matter.Engine;
		this.Render = Matter.Render;
		this.Composite = Matter.Composite;
		this.Bodies = Matter.Bodies;
		this.Runner = Matter.Runner;
		this.Mouse = Matter.Mouse;
		this.MouseConstraint = Matter.MouseConstraint;
		this.Common = Matter.Common;
		this.World = Matter.World;
		this.Constraint = Matter.Constraint;

		Matter.use(MatterAttractors);

		this.params = {
			isStatic: true,
			render: {
				fillStyle: "transparent"
			},
		};

		this.engine = this.Engine.create({
			gravity: {
				y: 0.4
			},
			enableSleeping: false
		});

		this.engine.positionIterations = 6;  // Réduit les petits ajustements de position
		this.engine.velocityIterations = 4;  // Empêche la détection de micro-mouvements
		this.engine.constraintIterations = 2;  // (Facultatif) Diminue les corrections des contraintes

		this.render = this.Render.create({
			element: this.keywordsContainer,
			engine: this.engine,
			options: {
				...this.canvasSizes,
				background: "transparent",
				wireframes: false
			}
		});

		// create runner
		let runner = this.Runner.create();
		this.Render.run(this.render);
		this.Runner.run(runner, this.engine);

		this.getBody();
	}

	static getBody() {
		// Helper pour créer les limites (murs) du canvas
		const angularVelocity = 0;
		const friction = 1;
		const frictionAir = 0.01;
		const frictionStatic = 1;
		const restitution = 0.4;
		const sleep = 1;

		const createBoundary = (x, y, width, height, options) =>
			this.Bodies.rectangle(x, y, width, height, options);

		// Helper pour créer les objet "cercle"
		const createCircleKeyword = (offsetX, offsetY, width, angle) => this.Composite.create({
			bodies: [
				this.Bodies.circle(offsetX, offsetY, width / 2, {
					angle,
					render: {fillStyle: "transparent"},
					angularVelocity: angularVelocity,
					friction: friction,         // Augmente la friction au sol
					frictionAir: frictionAir,     // Réduit les oscillations dans l'air
					frictionStatic: frictionStatic,
					restitution: restitution,       // Diminue le rebond
					sleepThreshold: sleep
				})
			]
		});

		// Helper pouyr créer les objets "pilule"
		const createPillKeyword = (offsetX, offsetY, width, height, groupId) => {
			const radius = height / 2;
			const pillBody = this.Bodies.rectangle(offsetX, offsetY, width - height, height, {
				render: {fillStyle: "transparent", strokeStyle: "transparent"},
				angularVelocity: angularVelocity,
				friction: friction,         // Augmente la friction au sol
				frictionAir: frictionAir,     // Réduit les oscillations dans l'air
				frictionStatic: frictionStatic,
				restitution: restitution,       // Diminue le rebond
				sleepThreshold: sleep,
				collisionFilter: {group: groupId}
			});

			const leftCircle = this.Bodies.circle(offsetX, offsetY, radius, {
				render: {fillStyle: "transparent", strokeStyle: "transparent"},
				angularVelocity: angularVelocity,
				friction: friction,         // Augmente la friction au sol
				frictionAir: frictionAir,     // Réduit les oscillations dans l'air
				frictionStatic: frictionStatic,
				restitution: restitution,       // Diminue le rebond
				sleepThreshold: sleep,
				collisionFilter: {group: groupId}
			});

			const rightCircle = this.Bodies.circle(offsetX, offsetY, radius, {
				render: {fillStyle: "transparent", strokeStyle: "transparent"},
				angularVelocity: angularVelocity,
				friction: friction,         // Augmente la friction au sol
				frictionAir: frictionAir,     // Réduit les oscillations dans l'air
				frictionStatic: frictionStatic,
				restitution: restitution,       // Diminue le rebond
				sleepThreshold: sleep,
				collisionFilter: {group: groupId}
			});

			// Les pilules sont composées d'un rectangle et 2 cercles liés entre eux par des points
			// de contraintes. Ces 3 sous objets définis ci-dessous font également parti d'un
			// même groupe unique pour qu'ils n'interagissent pas entre eux.
			const constraints = [
				this.Constraint.create({
					render: {fillStyle: "transparent", strokeStyle: "transparent"},
					bodyA: pillBody,
					pointA: {x: -(width - height) / 2, y: 0},
					bodyB: leftCircle,
					stiffness: 1, length: 0
				}),
				this.Constraint.create({
					render: {fillStyle: "transparent", strokeStyle: "transparent"},
					bodyA: pillBody,
					pointA: {x: (width - height) / 2, y: 0},
					bodyB: rightCircle,
					stiffness: 1, length: 0
				})
			];

			return this.Composite.create({
				bodies: [pillBody, leftCircle, rightCircle],
				constraints
			});
		};

		const {width: canvasWidth, height: canvasHeight} = this.canvasSizes;

		// Create boundaries for the canvas
		const top = createBoundary(canvasWidth / 2, -25, canvasWidth, 50, this.params);
		const wallRight = createBoundary(canvasWidth + 25, canvasHeight / 2, 50, canvasHeight, this.params);
		const floor = createBoundary(canvasWidth / 2, canvasHeight + 25, canvasWidth, 50, this.params);
		const wallLeft = createBoundary(-25, canvasHeight / 2, 50, canvasHeight, this.params);

		const wordElements = this.keywordsContainer.querySelectorAll(".keyword");

		this.wordBodies = [...wordElements].map((elemRef, index) => {
			// Les objets sont invisibles initialement
			gsap.to(elemRef, {opacity: 0, duration: 0});

			const elemRefWidth = elemRef.offsetWidth;
			const elemRefHeight = elemRef.offsetHeight;
			const randomAngle = 0.1 + Math.random() * 0.2; // Random angle
			const groupId = Matter.Body.nextGroup(true);

			// Offset de l'origine de chaque objet dans le canvas
			const offsetX = elemRefWidth + Math.random() * canvasWidth - elemRef.offsetWidth;
			const offsetY = elemRefHeight + Math.random() * (canvasHeight / 3);

			const keywordObject = elemRef.classList.contains('circle')
				? createCircleKeyword(offsetX, offsetY, elemRefWidth, randomAngle)
				: createPillKeyword(offsetX, offsetY, elemRefWidth, elemRefHeight, groupId);

			const keywordBodyObject = {
				body: keywordObject,
				elem: elemRef,
				render() {
					const bodies = this.body.bodies;
					if (bodies && bodies.length > 0) {
						const x = bodies.reduce((sum, body) => sum + body.position.x, 0) / bodies.length;
						const y = bodies.reduce((sum, body) => sum + body.position.y, 0) / bodies.length;
						this.elem.style.top = `${y - elemRefHeight / 2}px`;
						this.elem.style.right = `${canvasWidth - x - elemRefWidth / 2}px`;
						this.elem.style.transform = `rotate(${bodies[0].angle}rad)`;
					}
				}
			};

			// Animate opacity
			gsap.to(elemRef, {
				opacity: 1, duration: 0.6, delay: index * 0.02
			});

			return keywordBodyObject;
		});

		// Définition de l'intéraction avec la souris et ses contraintes
		const mouse = this.Mouse.create(this.render.canvas);
		const mouseConstraint = this.MouseConstraint.create(this.engine, {
			mouse,
			constraint: {stiffness: 0.6, render: {visible: false}}
		});

		// Add all bodies to the world
		this.Composite.add(this.engine.world, [
			...this.wordBodies.map((box) => box.body),
			top, floor, wallLeft, wallRight, mouseConstraint
		]);

		this.render.mouse = mouse;
		this.rerender();
	}

	static rerender() {
		this.wordBodies.forEach((element) => {
			element.render();
		});

		// Vérifie si tous les corps sont endormis pour éviter des micro-mouvements inutiles
		const bodies = this.engine.world.bodies;
		const allSleeping = bodies.every(body => body.isSleeping);
		if (!allSleeping) {
			this.Engine.update(this.engine);
		}

		this.resquestAnimation = requestAnimationFrame(this.rerender.bind(this));
	}

	static resize() {
		if (window.innerWidth > 768) {
			if (this.Composite && this.engine && this.engine.world) {
				cancelAnimationFrame(this.resquestAnimation);
				this.Composite.clear(this.engine.world);
				this.canvasSize();
				this.render.bounds.max.x = this.canvasSizes.width;
				this.render.bounds.max.y = this.canvasSizes.height;
				this.render.options.width = this.canvasSizes.width;
				this.render.options.height = this.canvasSizes.height;
				this.render.canvas.width = this.canvasSizes.width;
				this.render.canvas.height = this.canvasSizes.height;
				this.getBody();
			}
		} else {
			if (this.Composite && this.engine && this.engine.world) {
				cancelAnimationFrame(this.resquestAnimation);
				this.Composite.clear(this.engine.world);
			}
		}
	}
}
